// Material-UI Theme Configuration
// Black, Yellow, and White color scheme for Maverick Capital

import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    maverick: {
      yellow: string;
      black: string;
      white: string;
      darkGray: string;
      mediumGray: string;
      lightGray: string;
    };
  }
  interface PaletteOptions {
    maverick?: {
      yellow?: string;
      black?: string;
      white?: string;
      darkGray?: string;
      mediumGray?: string;
      lightGray?: string;
    };
  }
}

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#FFD700', // Yellow/Gold
      light: '#FFED4E',
      dark: '#C7A600',
      contrastText: '#000000',
    },
    secondary: {
      main: '#FFFFFF', // White
      light: '#FFFFFF',
      dark: '#E0E0E0',
      contrastText: '#000000',
    },
    background: {
      default: '#000000', // Black
      paper: '#1A1A1A', // Slightly lighter black for cards
    },
    text: {
      primary: '#FFFFFF', // White text
      secondary: '#E0E0E0', // Light gray for secondary text
    },
    error: {
      main: '#F44336',
    },
    warning: {
      main: '#FF9800',
    },
    info: {
      main: '#2196F3',
    },
    success: {
      main: '#4CAF50',
    },
    maverick: {
      yellow: '#FFD700',
      black: '#000000',
      white: '#FFFFFF',
      darkGray: '#1A1A1A',
      mediumGray: '#333333',
      lightGray: '#E0E0E0',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '3rem',
      fontWeight: 700,
      color: '#FFD700',
    },
    h2: {
      fontSize: '2.5rem',
      fontWeight: 700,
      color: '#FFD700',
    },
    h3: {
      fontSize: '2rem',
      fontWeight: 600,
      color: '#FFFFFF',
    },
    h4: {
      fontSize: '1.75rem',
      fontWeight: 600,
      color: '#FFFFFF',
    },
    h5: {
      fontSize: '1.5rem',
      fontWeight: 500,
      color: '#FFFFFF',
    },
    h6: {
      fontSize: '1.25rem',
      fontWeight: 500,
      color: '#FFFFFF',
    },
    body1: {
      fontSize: '1rem',
      color: '#FFFFFF',
    },
    body2: {
      fontSize: '0.875rem',
      color: '#E0E0E0',
    },
    button: {
      textTransform: 'none', // Don't uppercase buttons
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
        },
        contained: {
          backgroundColor: '#FFD700',
          color: '#000000',
          '&:hover': {
            backgroundColor: '#FFED4E',
          },
        },
        outlined: {
          borderColor: '#FFD700',
          color: '#FFD700',
          '&:hover': {
            borderColor: '#FFED4E',
            backgroundColor: 'rgba(255, 215, 0, 0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#1A1A1A',
          borderRadius: 12,
          border: '1px solid #333333',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#1A1A1A',
          backgroundImage: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#000000',
          borderBottom: '2px solid #FFD700',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#000000',
          borderRight: '2px solid #FFD700',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#1A1A1A',
          '& .MuiTableCell-head': {
            color: '#FFD700',
            fontWeight: 700,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(255, 215, 0, 0.05)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
        filled: {
          backgroundColor: '#FFD700',
          color: '#000000',
        },
        outlined: {
          borderColor: '#FFD700',
          color: '#FFD700',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: '#333333',
            },
            '&:hover fieldset': {
              borderColor: '#FFD700',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#FFD700',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#E0E0E0',
            '&.Mui-focused': {
              color: '#FFD700',
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#333333',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FFD700',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FFD700',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#333333',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#FFD700',
          '&:hover': {
            backgroundColor: 'rgba(255, 215, 0, 0.1)',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1A1A1A',
          color: '#FFFFFF',
          border: '1px solid #FFD700',
          fontSize: '0.875rem',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1A1A1A',
          borderRadius: 16,
          border: '2px solid #FFD700',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        standardSuccess: {
          backgroundColor: 'rgba(76, 175, 80, 0.2)',
          color: '#4CAF50',
        },
        standardError: {
          backgroundColor: 'rgba(244, 67, 54, 0.2)',
          color: '#F44336',
        },
        standardWarning: {
          backgroundColor: 'rgba(255, 152, 0, 0.2)',
          color: '#FF9800',
        },
        standardInfo: {
          backgroundColor: 'rgba(33, 150, 243, 0.2)',
          color: '#2196F3',
        },
      },
    },
  },
});

export default theme;
